import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from '@angular/cdk/menu';
import {isPlatformBrowser, NgOptimizedImage, NgStyle} from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  afterNextRender
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {IUserResponse} from '../../../features/auth/interfaces/user.interface';
import {AuthService} from '../../../features/auth/services/auth/auth.service';
import {NotificationService} from '../../services/notifications/notification.service';
import {SocketsService} from '../../services/sockets/sockets.service';
import {IconBurgerComponent} from '../icons/icon-burger/icon-burger.component';
import {IconCloseComponent} from '../icons/icon-close/icon-close.component';
import {IconFacebookComponent} from "../icons/icon-facebook/icon-facebook.component";
import {IconInstagramComponent} from "../icons/icon-instagram/icon-instagram.component";
import {IconLogoutComponent} from '../icons/icon-logout/icon-logout.component';
import {IconTwitterComponent} from "../icons/icon-twitter/icon-twitter.component";
import {
  IconUserRoundCogComponent
} from '../icons/icon-user-round-cog/icon-user-round-cog.component';
import {LanguageSwitcherComponent} from '../language-switcher/language-switcher.component';
import {NotificationsComponent} from "../notifications/notifications.component";

import { CartService } from '../../services/products/cart.service';
@Component({
  selector: 'braidsbook-header',
  imports: [
    NgOptimizedImage,
    IconBurgerComponent,
    LanguageSwitcherComponent,
    IconCloseComponent,
    NgStyle,
    RouterLink,
    RouterLinkActive,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    IconLogoutComponent,
    IconUserRoundCogComponent,
    NotificationsComponent,
    IconInstagramComponent,
    IconTwitterComponent,
    IconFacebookComponent,
    TranslatePipe
  ],
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, OnDestroy {
  #authService: AuthService = inject(AuthService);
  #cartService: CartService = inject(CartService);
  cartCount = this.#cartService.itemCount;
  #socketsService: SocketsService = inject(SocketsService);
  #notificationsService: NotificationService = inject(NotificationService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #platformId = inject(PLATFORM_ID);

  currentUser: Signal<IUserResponse | null> = this.#authService.currentUser;
  isAuthenticated = signal(false);
  // Passe à true uniquement après l'hydratation. Sert à n'afficher la navigation
  // dépendante du rôle qu'après l'hydratation, pour éviter un mismatch SSR/client
  // (le serveur n'a pas accès au localStorage, donc currentUser y est toujours null).
  isHydrated = signal(false);
  isMenuToggled: boolean = false;

  constructor() {
    afterNextRender(() => {
      // Mettre à jour l'état uniquement APRÈS le premier rendu/hydratation
      // Cela évite que le client essaie de changer le DOM pendant qu'il hydrate le HTML du serveur
      this.isAuthenticated.set(!!this.currentUser());
      this.isHydrated.set(true);
    });
  }

  ngOnInit(): void {
    // Initialiser les WebSockets et notifications si l’utilisateur est un coiffeur
    if (this.isAuthenticated() && this.currentUser()?.user_type === 'hairdresser') {
      this.initializeHairdresserServices();
    }
  }

  private initializeHairdresserServices(): void {
    // Connecter les WebSockets (async, mais on ne bloque pas)
    this.#socketsService.connect().catch(err => console.error('Erreur connexion WebSocket:', err));

    // Rejoindre la room du coiffeur pour les notifications ciblées
    if (this.currentUser()?.id) {
      this.#socketsService.joinRoom(`hairdresser - ${this.currentUser()!.id} `)
        .catch(err => console.error('Erreur join room:', err));
    }

    // Charger les notifications initiales
    this.#notificationsService.getAllNotifications()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
        },
        error: (error) => {
          console.error('Erreur lors du chargement des notifications:', error);
        }
      });

    // Demander la permission pour les notifications navigateur
    this.#notificationsService.requestNotificationPermission?.();
  }

  /**
   * Logout the user
   */
  logout(): void {
    if (this.currentUser()?.user_type === 'hairdresser') {
      // Reset du socket (quitte les rooms coiffeur) puis reconnexion
      // anonyme pour rester compté dans les sessions
      this.#socketsService.disconnect();
      this.#socketsService.connect().catch(() => {});
    }
    this.#authService.logout();
    this.isMenuToggled = false;

    // document existe uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      document.body.className = '';
    }
  }

  /**
   * Listen to the window resize event
   */
  @HostListener('window:resize')
  onResize(): void {
    // window et document existent uniquement côté client
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    if (this.isMenuToggled && window.innerWidth > 768) {
      this.isMenuToggled = false;
      document.body.className = '';
    }
  }

  /**
   * Toggles the mobile menu
   */
  openCart(): void {
    this.isMenuToggled = false;
    this.#cartService.open();
  }

  toggleMenu(): void {
    this.isMenuToggled = !this.isMenuToggled;

    // document existe uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      document.body.className = this.isMenuToggled ? 'overflow-hidden h-screen' : '';
    }
  }

  onNavigate(): void {
    this.isMenuToggled = false;

    // document existe uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      document.body.className = '';
    }
  }

  ngOnDestroy(): void {
    // document existe uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      document.body.className = '';
    }

    if (this.currentUser()?.user_type === 'hairdresser') {
      this.#socketsService.disconnect();
    }
  }

}
